import React, { useCallback, useState } from 'react';
import TurnstileWidget from './TurnstileWidget';

/**
 * WaitlistScreen — shown when new sign-ups are paused
 * (config/app.acceptingSignups === false). Instead of a hard wall, we
 * collect an email (bot-checked via Turnstile) so we can invite people
 * back when we reopen. Walter 2026-06-02.
 *
 * Existing users aren't affected — they can still sign in. This is only
 * the "you don't have an account yet and we're at capacity" path.
 */
export default function WaitlistScreen({ onBack }) {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState(null);
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'done' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [botError, setBotError] = useState(false);

  const handleVerify = useCallback((t) => { setToken(t); if (t) setBotError(false); }, []);
  const handleBotError = useCallback(() => setBotError(true), []);

  const submit = async (e) => {
    e?.preventDefault?.();
    const clean = email.trim().toLowerCase();
    if (!clean || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      setStatus('error'); setErrorMsg('Please enter a valid email address.'); return;
    }
    if (!token) {
      setStatus('error'); setErrorMsg('Please complete the bot-check below.'); return;
    }
    setStatus('submitting'); setErrorMsg('');
    try {
      const res = await fetch('/api/joinWaitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { email: clean, token } }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.result?.ok) {
        setStatus('done');
      } else {
        setStatus('error');
        setErrorMsg(body.error?.message || 'Something went wrong — please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error — please try again.');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', padding: 16,
      fontFamily: 'var(--mono)',
    }}>
      <div style={{
        width: '100%', maxWidth: 420,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: '28px 24px',
        boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
      }}>
        <div style={{ fontSize: 'calc(18px * var(--ui-scale, 1))', fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>
          <span style={{ color: 'var(--accent)' }}>listicity</span> is at capacity
        </div>

        {status === 'done' ? (
          <div style={{ fontSize: 'calc(13px * var(--ui-scale, 1))', color: 'var(--text)', lineHeight: 1.6 }}>
            You're on the list — thanks. We'll email <strong>{email.trim().toLowerCase()}</strong> the
            moment we open new sign-ups again.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 'calc(13px * var(--ui-scale, 1))', color: 'var(--text-dim)', lineHeight: 1.6, marginBottom: 18 }}>
              We've paused new sign-ups for a bit. Leave your email and we'll
              let you know the moment we reopen. Already have an account?{' '}
              {onBack
                ? <button type="button" onClick={onBack} style={linkBtn}>sign in</button>
                : 'just sign in.'}
            </div>

            <form onSubmit={submit}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                autoComplete="email"
                disabled={status === 'submitting'}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '10px 12px', marginBottom: 14,
                  border: '1px solid var(--border-bright)',
                  borderRadius: 'var(--radius)',
                  background: 'var(--bg)', color: 'var(--text)',
                  fontFamily: 'var(--mono)', fontSize: 'calc(13px * var(--ui-scale, 1))',
                }}
              />

              <div style={{ marginBottom: 14 }}>
                <TurnstileWidget onVerify={handleVerify} onError={handleBotError} />
                {botError && (
                  <div style={{ fontSize: 'calc(11px * var(--ui-scale, 1))', color: 'var(--danger, #d04a4a)', marginTop: 4 }}>
                    Bot-check couldn't load. Refresh and try again.
                  </div>
                )}
              </div>

              {status === 'error' && errorMsg && (
                <div style={{ fontSize: 'calc(12px * var(--ui-scale, 1))', color: 'var(--danger, #d04a4a)', marginBottom: 12 }}>
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                style={{
                  width: '100%', padding: '11px 16px',
                  border: '1px solid var(--accent)',
                  background: 'var(--accent)', color: 'var(--bg)',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--mono)', fontSize: 'calc(13px * var(--ui-scale, 1))',
                  fontWeight: 600, cursor: status === 'submitting' ? 'default' : 'pointer',
                  opacity: status === 'submitting' ? 0.7 : 1,
                }}
              >
                {status === 'submitting' ? 'adding you…' : 'notify me'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const linkBtn = {
  background: 'transparent', border: 'none', padding: 0,
  color: 'var(--accent)', cursor: 'pointer',
  fontFamily: 'var(--mono)', fontSize: 'inherit', textDecoration: 'underline',
};
